import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { formatReadingData, readingTypeLabel } from './formatReading';

// PHA-871: patient-facing RPM readings PDF.
// HARD RULE — this document contains NO PII/MRN: no patient name, no patient_id,
// no DOB, no device ID. Only the readings, the date range, and clinic branding.

export interface RpmReading {
  time: string;
  reading_type: string;
  data: Record<string, any>;
}

export interface RpmReadingsPdfOptions {
  clinicName?: string;
  logoPath?: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

// Strip characters jsPDF's core fonts can't render, normalize smart punctuation.
const sanitize = (text: string): string =>
  (text ?? '')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/₂/g, '2') // SpO₂ subscript -> 2, so "SpO2" renders
    .replace(/[^\x20-\x7E]/g, ''); // keep printable ASCII only (jsPDF core fonts)

// Parse a YYYY-MM-DD as local noon to avoid TZ date-shift, then format for the header.
const formatDayLabel = (ymd: string): string => {
  const d = new Date(`${ymd}T12:00:00`);
  return Number.isNaN(d.getTime()) ? ymd : format(d, 'MMM d, yyyy');
};

/** Loads an image URL into a base64 data URL for embedding; null on any failure. */
async function loadLogoDataUrl(url?: string): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Generates a Remote Patient Monitoring readings PDF for the given (already
 * date-filtered) readings and returns it as a Blob. The caller decides whether
 * to download it or upload it for sharing.
 */
export async function generateRpmReadingsPdf(
  readings: RpmReading[],
  opts: RpmReadingsPdfOptions,
): Promise<Blob> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = 20;

  // ---- Branding: clinic logo, else clinic name ----
  const logoDataUrl = await loadLogoDataUrl(opts.logoPath);
  let brandingRendered = false;
  if (logoDataUrl) {
    try {
      const maxW = 50;
      const maxH = 16;
      const props = doc.getImageProperties(logoDataUrl);
      if (!props.width || !props.height) throw new Error('invalid image dimensions');
      const scale = Math.min(maxW / props.width, maxH / props.height);
      const drawW = props.width * scale;
      const drawH = props.height * scale;
      const x = (pageWidth - drawW) / 2;
      const fmt = (logoDataUrl.match(/^data:image\/([^;]+)/)?.[1] ?? '').toUpperCase();
      doc.addImage(logoDataUrl, fmt, x, yPos, drawW, drawH);
      yPos += drawH + 8;
      brandingRendered = true;
    } catch {
      /* fall through to text fallback */
    }
  }
  if (!brandingRendered && opts.clinicName) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(sanitize(opts.clinicName), pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
  }

  // ---- Title (line 1) + date range (line 2) ----
  // PHA-878: split the crowded single-line header. Title on the first line,
  // the date range on the second (no "from" prefix).
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const title = 'Remote Patient Monitoring Readings';
  const titleLines = doc.splitTextToSize(sanitize(title), contentWidth);
  titleLines.forEach((line: string, i: number) => {
    doc.text(line, pageWidth / 2, yPos + i * 7, { align: 'center' });
  });
  yPos += (titleLines.length - 1) * 7 + 8;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  const dateRange = `${formatDayLabel(opts.startDate)} to ${formatDayLabel(opts.endDate)}`;
  doc.text(sanitize(dateRange), pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;

  doc.setLineWidth(0.5);
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // ---- Blood Pressure chart (BP-only per PHA-871) ----
  const bp = readings
    .filter((r) => r.reading_type === 'BloodPressure')
    .map((r) => ({
      t: new Date(r.time).getTime(),
      systolic: Number(r.data?.systolic),
      diastolic: Number(r.data?.diastolic),
      pulse: Number(r.data?.pulse),
    }))
    .filter((p) => !Number.isNaN(p.t))
    .sort((a, b) => a.t - b.t);

  if (bp.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Blood Pressure', margin, yPos);
    yPos += 4;

    const chartX = margin;
    const chartY = yPos;
    const chartW = contentWidth;
    const chartH = 60;
    const plotX = chartX + 12; // leave room for y-axis labels
    const plotY = chartY;
    const plotW = chartW - 12;
    const plotH = chartH;

    // value range across all three series (with a little padding)
    const allVals = bp.flatMap((p) => [p.systolic, p.diastolic, p.pulse]).filter((v) => !Number.isNaN(v));
    let vMin = Math.min(...allVals);
    let vMax = Math.max(...allVals);
    if (!Number.isFinite(vMin) || !Number.isFinite(vMax)) {
      vMin = 0;
      vMax = 100;
    }
    const pad = Math.max(5, (vMax - vMin) * 0.1);
    vMin = Math.floor((vMin - pad) / 10) * 10;
    vMax = Math.ceil((vMax + pad) / 10) * 10;
    const vRange = vMax - vMin || 1;

    const tMin = bp[0].t;
    const tMax = bp[bp.length - 1].t;
    const tRange = tMax - tMin || 1;

    const xFor = (t: number) => (bp.length === 1 ? plotX + plotW / 2 : plotX + ((t - tMin) / tRange) * plotW);
    const yFor = (v: number) => plotY + plotH - ((v - vMin) / vRange) * plotH;

    // gridlines + y labels (5 ticks)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    for (let i = 0; i <= 4; i++) {
      const val = vMin + (vRange * i) / 4;
      const gy = yFor(val);
      doc.setDrawColor(230, 230, 230);
      doc.line(plotX, gy, plotX + plotW, gy);
      doc.text(String(Math.round(val)), plotX - 2, gy + 2, { align: 'right' });
    }
    // axes
    doc.setDrawColor(150, 150, 150);
    doc.line(plotX, plotY, plotX, plotY + plotH);
    doc.line(plotX, plotY + plotH, plotX + plotW, plotY + plotH);

    const series: Array<{ key: 'systolic' | 'diastolic' | 'pulse'; color: [number, number, number]; label: string }> = [
      { key: 'systolic', color: [239, 68, 68], label: 'Systolic' },
      { key: 'diastolic', color: [59, 130, 246], label: 'Diastolic' },
      { key: 'pulse', color: [34, 197, 94], label: 'Pulse' },
    ];

    doc.setLineWidth(0.5);
    series.forEach((s) => {
      doc.setDrawColor(...s.color);
      doc.setFillColor(...s.color);
      let prev: { x: number; y: number } | null = null;
      bp.forEach((p) => {
        const v = p[s.key];
        if (Number.isNaN(v)) {
          prev = null;
          return;
        }
        const x = xFor(p.t);
        const y = yFor(v);
        if (prev) doc.line(prev.x, prev.y, x, y);
        doc.circle(x, y, 0.6, 'F');
        prev = { x, y };
      });
    });

    yPos = plotY + plotH + 8;

    // legend
    doc.setFontSize(8);
    let lx = plotX;
    series.forEach((s) => {
      doc.setDrawColor(...s.color);
      doc.setFillColor(...s.color);
      doc.rect(lx, yPos - 2.5, 3, 3, 'F');
      doc.setTextColor(60, 60, 60);
      doc.text(s.label, lx + 4.5, yPos);
      lx += doc.getTextWidth(s.label) + 12;
    });
    doc.setTextColor(0, 0, 0);
    yPos += 10;
  }

  // ---- Readings table: Date & Time | Reading Type | Measured Results ----
  const colWidths = [50, 40, contentWidth - 90];
  const headers = ['Date & Time', 'Reading Type', 'Measured Results'];

  const drawRow = (cells: string[], isHeader: boolean): void => {
    doc.setFontSize(9);
    doc.setFont('helvetica', isHeader ? 'bold' : 'normal');
    const cellPadding = 2;
    const lineHeight = 4.5;

    const wrapped = cells.map((c, i) =>
      doc.splitTextToSize(sanitize(c || ''), colWidths[i] - 2 * cellPadding),
    );
    const rowHeight = Math.max(lineHeight, ...wrapped.map((l) => l.length * lineHeight));

    if (yPos + rowHeight + cellPadding * 2 > pageHeight - 20) {
      doc.addPage();
      yPos = 20;
    }

    if (isHeader) {
      doc.setFillColor(235, 235, 235);
      doc.rect(margin, yPos - cellPadding, colWidths.reduce((a, b) => a + b, 0), rowHeight + cellPadding * 2, 'F');
    }
    let cx = margin;
    doc.setDrawColor(210, 210, 210);
    colWidths.forEach((w) => {
      doc.rect(cx, yPos - cellPadding, w, rowHeight + cellPadding * 2, 'S');
      cx += w;
    });
    cx = margin;
    wrapped.forEach((lines, i) => {
      let ty = yPos + lineHeight * 0.8;
      lines.forEach((line: string) => {
        doc.text(line, cx + cellPadding, ty);
        ty += lineHeight;
      });
      cx += colWidths[i];
    });
    yPos += rowHeight + cellPadding * 2;
  };

  drawRow(headers, true);
  const sorted = [...readings].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  sorted.forEach((r) => {
    drawRow(
      [
        format(new Date(r.time), 'MMM d, yyyy h:mm a'),
        readingTypeLabel(r.reading_type),
        formatReadingData(r.reading_type, r.data),
      ],
      false,
    );
  });

  return doc.output('blob');
}

export default generateRpmReadingsPdf;
